import React, { useState } from "react";
import { Badge, Box, Button, Collapse, HStack, Text, VStack } from "@chakra-ui/react";
import {
  formatOrderRefs,
  useExecutionHistory,
  type ExecutionHistoryEntry,
} from "../config/executionHistoryStore";
import { formatDuration } from "../flow/estimateTime";
import { copyText, formatClock } from "./statusMeta";
import useNotification from "../../../utils/useNotification";

const STATUS_SCHEME: Record<ExecutionHistoryEntry["status"], string> = {
  completed: "green",
  failed: "red",
  cancelled: "gray",
};

const formatWhen = (ts: number) =>
  new Date(ts).toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const LOG_COLOR: Record<string, string> = {
  info: "gray.200",
  success: "green.300",
  warn: "yellow.300",
  error: "red.300",
  debug: "gray.500",
};

interface Props {
  onViewOrder: (orderNo: string) => void;
}

const HistoryRow: React.FC<{
  entry: ExecutionHistoryEntry;
  open: boolean;
  onToggle: () => void;
  onViewOrder: (orderNo: string) => void;
}> = ({ entry, open, onToggle, onViewOrder }) => {
  const toast = useNotification();
  const orderNo = entry.childOrderNo || entry.enquiryOrderNo;
  const scheme = STATUS_SCHEME[entry.status];
  const refs = formatOrderRefs({
    enquiryNo: entry.enquiryOrderNo,
    enquiryId: entry.enquiryId,
    orderNo: entry.childOrderNo,
    orderId: entry.childOrderId,
  });
  const logText = [refs, ...entry.log.map((line) => `${formatClock(line.at)}  ${line.message}`)]
    .filter(Boolean)
    .join("\n");

  return (
    <Box borderWidth="1px" borderColor={`${scheme}.200`} borderRadius="lg" bg="white" overflow="hidden">
      <Box px={4} py={3} cursor="pointer" onClick={onToggle} _hover={{ bg: "gray.50" }}>
        <HStack justify="space-between" align="flex-start" spacing={3} mb={1}>
          <Badge colorScheme={scheme} fontSize="10px" textTransform="capitalize" flexShrink={0}>
            {entry.status}
          </Badge>
          <Text fontSize="xs" color="gray.400" flexShrink={0}>
            {open ? "Hide log" : "Show log"}
          </Text>
        </HStack>
        <Text fontSize="sm" fontWeight="700" color="gray.800" fontFamily="mono" wordBreak="break-all">
          {orderNo || "No order yet"}
        </Text>
        {entry.targetLabel && (
          <Text fontSize="xs" color="gray.600" mt={0.5}>
            Target: {entry.targetLabel}
          </Text>
        )}
        <Text fontSize="xs" color="gray.500" mt={1} lineHeight="1.5">
          {formatWhen(entry.startedAt)}
          {entry.durationMs != null ? `  ·  ${formatDuration(entry.durationMs)}` : ""}
          {`  ·  ${entry.completedSteps} done`}
          {entry.skippedSteps ? `  ·  ${entry.skippedSteps} skipped` : ""}
        </Text>
        {(entry.customerName || entry.supplierName) && (
          <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={2}>
            {[entry.customerName, entry.supplierName, entry.supplierFlow].filter(Boolean).join("  ·  ")}
          </Text>
        )}
        {entry.status === "failed" && entry.lastError && (
          <Text fontSize="xs" color="red.600" mt={1} noOfLines={2}>
            {entry.failedStepName ? `${entry.failedStepName}: ` : ""}
            {entry.lastError}
          </Text>
        )}
      </Box>

      <Collapse in={open} animateOpacity unmountOnExit>
        <Box px={4} pb={4} borderTopWidth="1px" borderColor="gray.100" pt={3}>
          <HStack mb={2} spacing={2} flexWrap="wrap">
            {orderNo && (
              <>
                <Button size="xs" variant="outline" onClick={() => onViewOrder(orderNo)}>
                  View order
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={async () => {
                    const ok = await copyText(orderNo);
                    toast({ status: ok ? "success" : "error", description: ok ? `${orderNo} copied` : "Copy failed" });
                  }}
                >
                  Copy order
                </Button>
              </>
            )}
            {entry.orderStatus && (
              <Badge variant="subtle" fontSize="9px">
                {entry.orderStatus}
              </Badge>
            )}
            {entry.log.length > 0 && (
              <Button
                size="xs"
                variant="ghost"
                ml="auto"
                onClick={async () => {
                  const ok = await copyText(logText);
                  toast({ status: ok ? "success" : "error", description: ok ? "Log copied" : "Copy failed" });
                }}
              >
                Copy log
              </Button>
            )}
          </HStack>

          {refs && (
            <Box bg="gray.800" color="blue.200" fontFamily="mono" fontSize="11px" px={3} py={2} borderTopRadius="md">
              {refs}
            </Box>
          )}
          <Box
            bg="gray.900"
            color="gray.100"
            fontFamily="mono"
            fontSize="11px"
            maxH="280px"
            overflowY="auto"
            borderBottomRadius="md"
            borderTopRadius={refs ? undefined : "md"}
            px={3}
            py={2}
          >
            {entry.log.length === 0 && <Text color="gray.500">No log entries saved.</Text>}
            {entry.log.map((line, i) => (
              <Text
                key={`${line.at}-${i}`}
                color={LOG_COLOR[line.level] || "gray.300"}
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                lineHeight="1.55"
                py="1px"
              >
                <Text as="span" color="gray.500">
                  {formatClock(line.at)}{" "}
                </Text>
                {line.message}
              </Text>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const ExecutionHistory: React.FC<Props> = ({ onViewOrder }) => {
  const entries = useExecutionHistory((s) => s.entries);
  const clear = useExecutionHistory((s) => s.clear);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
      <HStack justify="space-between" mb={3} align="center">
        <Box>
          <Text fontWeight="700" fontSize="sm">
            Recent runs
          </Text>
          <Text fontSize="xs" color="gray.500">
            Last {entries.length} of 10 saved locally
          </Text>
        </Box>
        <Button size="xs" variant="ghost" onClick={clear} isDisabled={!entries.length}>
          Clear
        </Button>
      </HStack>
      {entries.length === 0 ? (
        <Text fontSize="xs" color="gray.500">
          Finished executions appear here. Nothing stored yet.
        </Text>
      ) : (
        <VStack align="stretch" spacing={3}>
          {entries.map((entry) => (
            <HistoryRow
              key={entry.id}
              entry={entry}
              open={openId === entry.id}
              onToggle={() => setOpenId((id) => (id === entry.id ? null : entry.id))}
              onViewOrder={onViewOrder}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default ExecutionHistory;
